'use client';

import React, { useRef, useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

const pillars = [
  {
    id: 'pillar-wellness',
    icon: 'HeartIcon',
    title: 'Wellness',
    description: 'Products thoughtfully designed to support your everyday wellbeing — from within and without. We believe true wellness is a daily practice, not a destination.',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    border: 'border-emerald-100 dark:border-emerald-900/40',
  },
  {
    id: 'pillar-beauty',
    icon: 'SparklesIcon',
    title: 'Beauty',
    description: 'Premium beauty and personal-care experiences crafted with the finest ingredients. Elevate your daily rituals with formulations that truly perform.',
    color: 'text-rose-500',
    bg: 'bg-rose-50 dark:bg-rose-950/30',
    border: 'border-rose-100 dark:border-rose-900/40',
  },
  {
    id: 'pillar-knowledge',
    icon: 'AcademicCapIcon',
    title: 'Knowledge',
    description: 'Education that empowers informed choices. The Richésse Academy delivers product knowledge, wellness science, and business skills in one place.',
    color: 'text-blue-600',
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-100 dark:border-blue-900/40',
  },
  {
    id: 'pillar-community',
    icon: 'UserGroupIcon',
    title: 'Community',
    description: 'A global network of people growing together. Share experiences, celebrate wins, and support each other on the journey toward a richer life.',
    color: 'text-violet-600',
    bg: 'bg-violet-50 dark:bg-violet-950/30',
    border: 'border-violet-100 dark:border-violet-900/40',
  },
  {
    id: 'pillar-opportunity',
    icon: 'TrophyIcon',
    title: 'Opportunity',
    description: 'A platform for individuals who want to build something meaningful. Your own storefront, your own brand, your own path — powered by Richésse.',
    color: 'text-amber-600',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-100 dark:border-amber-900/40',
  },
];

export default function PhilosophySection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 lg:py-32 bg-background" ref={ref}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-16 lg:mb-20">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="section-divider" />
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">Our Philosophy</span>
            <span className="section-divider" />
          </div>
          <h2 className="font-display text-section-title text-foreground mb-4">
            More Than Products. A Lifestyle.
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-base leading-relaxed">
            Richésse brings together five pillars that define who we are and why our members thrive.
          </p>
        </div>

        {/* Pillars grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {pillars.map((pillar, i) => (
            <div
              key={pillar.id}
              className={`card-elevated card-hover p-6 transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className={`w-12 h-12 rounded-xl ${pillar.bg} border ${pillar.border} flex items-center justify-center mb-4`}>
                <Icon name={pillar.icon as any} size={22} className={pillar.color} />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-2">{pillar.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{pillar.description}</p>
            </div>
          ))}
        </div>

        {/* Brand intro block */}
        <div className="mt-16 rounded-2xl bg-secondary border border-border p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div>
              <span className="text-xs font-semibold tracking-widest uppercase text-primary mb-3 block">Our Mission</span>
              <h3 className="font-display text-hero-lg text-foreground mb-4">
                Richesse is the future of personal commerce.
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                We built a platform where personal passion meets professional opportunity. Every member receives a fully personalized storefront, a curated product catalog, and the tools to build a genuine business — all under the Richésse brand.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Premium Products', 'Personal Storefront', 'Real Education', 'Global Community'].map(tag => (
                  <span key={`tag-${tag}`} className="text-xs font-medium px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { id: 'stat-revenue', value: '$24M+', label: 'Member Sales Volume', sub: 'Last 12 months' },
                { id: 'stat-active', value: '48,241', label: 'Active Members', sub: 'Across 32 countries' },
                { id: 'stat-nps', value: '94%', label: 'Member Satisfaction', sub: 'Based on Q3 2026 survey' },
                { id: 'stat-products', value: '127', label: 'Premium Products', sub: 'Across 5 categories' },
              ].map(stat => (
                <div key={stat.id} className="card-elevated p-5">
                  <p className="text-2xl font-bold text-foreground tabular-nums mb-1">{stat.value}</p>
                  <p className="text-sm font-semibold text-foreground mb-0.5">{stat.label}</p>
                  <p className="text-xs text-muted-foreground">{stat.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}